import { type ReactNode, type ReactElement } from 'react';
import { Globe, Wrench, Activity } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

/** The three internal tabs on the Shadow AI Tools surface. */
export type ShadowAiToolsTabValue = 'browser' | 'installed' | 'running';

const TAB_TRIGGER_CLASS =
  'flex items-center gap-1 !whitespace-normal sm:!whitespace-nowrap text-xs sm:text-sm h-auto sm:h-[calc(100%-1px)] py-2 sm:py-1';

interface ShadowAiToolsTabsProps {
  /** Active tab value (a `ShadowAiToolsTabValue`; typed as string for Radix). */
  value: string;
  onValueChange: (value: string) => void;
  /** Table rendered under each tab — content differs between org and personal. */
  browser: ReactNode;
  installed: ReactNode;
  running: ReactNode;
}

/**
 * Shared Browser / Installed / Running tab bar for the Shadow AI Tools surface,
 * used by both the org Tools page and the personal dashboard so the tab order,
 * labels, icons, and styling stay identical in one place. Each surface supplies
 * its own per-tab table content via the `browser` / `installed` / `running`
 * props.
 */
export function ShadowAiToolsTabs({
  value,
  onValueChange,
  browser,
  installed,
  running,
}: ShadowAiToolsTabsProps): ReactElement {
  return (
    <Tabs value={value} onValueChange={onValueChange} className="gap-6">
      <TabsList className="grid w-full grid-cols-1 h-auto sm:h-9 sm:grid-cols-3">
        <TabsTrigger value="browser" className={TAB_TRIGGER_CLASS}>
          <Globe className="size-5 shrink-0" />
          <span className="break-words text-center">Browser AI Tools</span>
        </TabsTrigger>
        <TabsTrigger value="installed" className={TAB_TRIGGER_CLASS}>
          <Wrench className="size-5 shrink-0" />
          <span className="break-words text-center">Installed AI Tools</span>
        </TabsTrigger>
        <TabsTrigger value="running" className={TAB_TRIGGER_CLASS}>
          <Activity className="size-5 shrink-0" />
          <span className="break-words text-center">Running Now</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="browser">{browser}</TabsContent>
      <TabsContent value="installed">{installed}</TabsContent>
      <TabsContent value="running">{running}</TabsContent>
    </Tabs>
  );
}
